import { Column, FormContext, Row } from '@/components/SettingsLayout';
import styles from '../styles.module.css';
import { Button, Divider } from '@/ui';
import { useTranslation } from 'react-i18next';
import { User } from '@/types';
import { Company } from '@/context/types';
import { Country } from 'country-state-city';
import { useUpdateCompanyAndOwnerMutation } from '@/store/slices/companySlice';
import { UpdateCompanyDto } from '@/services';
import toast from 'react-hot-toast';
import { industriesDropDown } from '@/pages/accounts/components/CreateOrgForm';
import { SelectOption } from '@/types/types';
import { CompanyFormFields } from './CompanyFormFields';
import DeferredFeesSetting from './DeferredFeesSetting';
import { useGetPlanByCompanyIdQuery, useGetPlansQuery } from '@/store/slices/plansSlice';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useParams } from 'react-router-dom';
import CompanySkeleton from './CompanySkeleton';
import { useGetUser } from '@/hooks';

type Props = {
  company: Company;
  companyOwner: User;
};

// Define a proper type for form data
type CompanyFormData = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company_name: string;
  industry: SelectOption;
  country: SelectOption;
  currency: string;
  address: string;
  city: string;
  postCode: string;
  shipping: {
    address: string;
    address2: string;
    city: string;
    postCode: string;
  };
};

function CompanyProfile({ company, companyOwner }: Props) {
  const { t } = useTranslation();
  const { id } = useParams();
  const { currentUser } = useGetUser();
  const [updateCompanyAndOwner] = useUpdateCompanyAndOwnerMutation();
  const { data: plans, isLoading: isPlansLoading } = useGetPlansQuery(
    company.organizationId
      ? {
          organizationId: company.organizationId,
        }
      : skipToken,
  );
  const {
    data: singlePlan,
    isLoading: isSinglePlanLoading,
    refetch: refetchPlan,
  } = useGetPlanByCompanyIdQuery(
    id && plans && currentUser?.companyId
      ? { companyId: id, organizationId: currentUser.companyId }
      : skipToken,
  );

  const onSubmit = (data: Record<string, any>) => {
    const formData = data as CompanyFormData;
    const updateCompanyAndOwnerDto: UpdateCompanyDto = {
      id: company._id,
      ownerId: companyOwner._id,
      organizationId: currentUser.companyId,
      body: {
        company: {
          name: formData.company_name,
          address: {
            ...company.address,
            address1: formData.address,
            city: formData.city,
            country: formData.country?.value || company.address.country,
            postCode: formData.postCode,
          },
          settings: {
            ...company.settings,
          },
          shipping: {
            ...company.shipping,
            address1: formData.shipping.address,
            address2: formData.shipping.address2,
            city: formData.shipping.city,
            postCode: formData.shipping.postCode,
          },
          industry: formData.industry?.value || company.industry || '',
        },
        owner: {
          firstName: formData.first_name,
          lastName: formData.last_name,
          phone: formData.phone,
        },
      },
      planId: data.plan?.value || '',
    };
    updateCompanyAndOwner(updateCompanyAndOwnerDto)
      .unwrap()
      .then(() => {
        toast.success(t('Companies.company_update_success'));
        refetchPlan();
      })
      .catch(() => {
        toast.error(t('Companies.company_update_error'));
      });
  };

  const getCountryIndustry = (industry: string): SelectOption | undefined => {
    const defaultIndustry = industriesDropDown.find((i: SelectOption) => i.value === industry);
    if (defaultIndustry) {
      return {
        label: t(`Accounts.${defaultIndustry.label}`),
        value: defaultIndustry.value,
      };
    } else {
      return undefined;
    }
  };

  const countryCode = company.address.country;
  const countryData = countryCode ? Country.getCountryByCode(countryCode) : null;

  if (isPlansLoading || isSinglePlanLoading) {
    return <CompanySkeleton />;
  }

  return (
    <FormContext
      submitHandler={onSubmit}
      defaultValues={{
        first_name: companyOwner.firstName || '',
        last_name: companyOwner.lastName || '',
        email: companyOwner.email || '',
        phone: companyOwner.phone || '',
        company_name: company.name || '',
        industry: company.industry ? getCountryIndustry(company.industry) : undefined,
        country: countryData
          ? {
              label: countryData.name,
              value: countryData.isoCode,
            }
          : undefined,
        currency: company.settings?.currency ?? '',
        address: company.address?.address1 ?? '',
        city: company.address.city || '',
        postCode: company.address.postCode ?? '',
        shipping: {
          address: company.shipping?.address1 ?? '',
          address2: company.shipping?.address2 ?? '',
          city: company.shipping?.city ?? '',
          postCode: company.shipping?.postCode || '',
        },
        plan: singlePlan
          ? {
              label: singlePlan.name,
              value: singlePlan._id,
            }
          : undefined,
      }}
    >
      <div className={styles.companyProfileContainer}>
        <Column gapSize='20'>
          <CompanyFormFields mode='edit' plans={plans} />
          <DeferredFeesSetting company={company} />
          <Divider />
          <Row justify='justify-end'>
            {/* TODO: Add delete button when we have the delete company endpoint */}
            {/* <Button variant='danger' type='button'>
            {t('Shared.delete')}
          </Button> */}
            <Row fullWidth={false} gapSize='8'>
              <Button variant='outline' type='button'>
                {t('Shared.cancel')}
              </Button>
              <Button variant='primary' type='submit'>
                {t('Shared.update')}
              </Button>
            </Row>
          </Row>
        </Column>
      </div>
    </FormContext>
  );
}

export default CompanyProfile;
